// MACHINERY V2 (Stroje) — the rail's VÝROBCA section (M2.8). Split out of
// CatalogFilterRail.tsx for the 300-line cap.
//
// Spec: data package §11 `manufacturerFacet.*` + `query.manufacturerId`
// ("Parent filters all children"). Artboards 0900 / 0901 draw it: an
// "Všetky značky" row, then parent families with expandable children, then the
// flat tail, then "Zobraziť všetkých N →".
//
// The grouping and the cap live in catalogFacets.ts (pure); this file is the
// rendering, built on the fleet rail's exported `Row` / `Section` so a brand
// row and a fleet-status row are literally the same row.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Row, Section } from "../fleet/FilterRail";
import {
  CatalogFacetModel,
  CatalogFacetRow,
  catalogFacetCapBypassed,
  visibleFacetRows,
} from "./catalogFacets";

export interface CatalogBrandFacetsProps {
  facets: CatalogFacetModel;
  selectedManufacturerId?: string;
  onSelect: (id?: string) => void;
  brandSearch: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  expandedParents: Set<string>;
  onToggleParent: (id: string) => void;
  /** Total across the current category — the "all brands" row's count. */
  totalCount: number;
}

/**
 * The parent expander sits INSIDE the brand row, whose own click selects the
 * brand — so the chevron must swallow BOTH activation paths or expanding a
 * family would also filter by it. This is the Wave-4 C4 rule (mouse AND
 * keyboard) applied to a nested control rather than to a card.
 */
const ParentToggle: React.FC<{ open: boolean; label: string; onToggle: () => void }> = ({
  open,
  label,
  onToggle,
}) => {
  const { translate } = useTranslation("myMachinery");
  const activate = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <span
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-label={`${label} — ${translate(
        open ? "myMachinery:collapseAll" : "myMachinery:expandAll"
      )}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate(e);
        }
      }}
      style={{
        display: "inline-grid",
        placeItems: "center",
        width: 14,
        height: 14,
        cursor: "pointer",
        color: "var(--faint)",
        flexShrink: 0,
      }}
    >
      <svg
        viewBox="0 0 12 12"
        width="10"
        height="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden
      >
        <path
          d={open ? "M3 7.5 L6 4.5 L9 7.5" : "M4.5 3 L7.5 6 L4.5 9"}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
};

const CatalogBrandFacets: React.FC<CatalogBrandFacetsProps> = ({
  facets,
  selectedManufacturerId,
  onSelect,
  brandSearch,
  expanded,
  onToggleExpanded,
  expandedParents,
  onToggleParent,
  totalCount,
}) => {
  const { translate } = useTranslation("myMachinery");

  const rows: CatalogFacetRow[] = visibleFacetRows(facets, expanded, brandSearch);
  const hiddenRows = facets.totalRows - rows.length;
  /** A brand search shows every match regardless of `expanded`, so while one is
   *  running there is nothing to expand and nothing to collapse — and a control
   *  that reports a state it cannot change is worse than no control. It comes
   *  back, with the user's own choice intact, the moment the search is cleared.
   *  The predicate is the row builder's own (catalogFacets.ts), never a second
   *  copy of the condition. */
  const capBypassed = catalogFacetCapBypassed(brandSearch);

  /** A parent opens when the user asked OR a brand search matched inside it
   *  (catalogFacets.ts `matchedParentIds`) — the match must be visible without
   *  a second click. */
  const isParentOpen = (id: string) => expandedParents.has(id) || facets.matchedParentIds.has(id);

  const toggle = (id?: string) => onSelect(selectedManufacturerId === id ? undefined : id);

  return (
    <Section title={translate("myMachinery:brand")}>
      <Row
        label={translate("myMachinery:allBrands")}
        count={totalCount}
        active={!selectedManufacturerId}
        onClick={() => onSelect(undefined)}
      />
      {rows.map((row) => (
        <React.Fragment key={row.id}>
          <Row
            label={row.name}
            count={row.count}
            active={selectedManufacturerId === row.id}
            icon={
              row.children && row.children.length > 0 ? (
                <ParentToggle
                  open={isParentOpen(row.id)}
                  label={row.name}
                  onToggle={() => onToggleParent(row.id)}
                />
              ) : undefined
            }
            onClick={() => toggle(row.id)}
          />
          {row.children &&
            isParentOpen(row.id) &&
            row.children.map((child) => (
              <Row
                key={child.id}
                label={child.name}
                count={child.machineCount}
                depth={1}
                active={selectedManufacturerId === child.id}
                onClick={() => toggle(child.id)}
              />
            ))}
        </React.Fragment>
      ))}
      {!capBypassed && (hiddenRows > 0 || expanded) && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onToggleExpanded}
          style={{ height: 26, marginTop: 2, fontSize: 11.5, justifyContent: "flex-start" }}
        >
          {expanded
            ? translate("myMachinery:v2.catalog.showFewerBrands")
            : translate("myMachinery:v2.catalog.showAllBrands", { count: facets.totalRows })}
        </button>
      )}
    </Section>
  );
};

export default CatalogBrandFacets;
